import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useUrlTableState from "../../../hooks/useUrlTableState";
import LeadSelectionModal from "./LeadSelectionModal";
import { useAuth } from "../../../context/AuthContext";
import { USER_ROLES } from "../../../utils/constants";
import leadActivityService from "../../../api/crm/leads/leadActivityService";
import { toast } from "../../../utils/toast";
import {
  Plus,
  Phone,
  Mail,
  MessageSquare,
  Calendar,
  FileText,
  Linkedin,
  Clock,
  User,
  Filter,
  Search,
  Download,
  Eye,
  Edit,
  Trash2,
  X,
  XCircle,
  CheckCircle,
  AlertCircle,
  Building,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Activity,
} from "lucide-react";
import Button from "../../../components/common/Button/Button";
import {
  formatDate,
} from "../../../utils/stringUtils";
import {
  sanitizeSearchInput,
  sanitizeDescriptionInput,
  handleSearchInputChange,
  getCharacterCount,
  FIELD_LIMITS,
} from "../../../utils/inputValidation";
import PermissionGate from "../../../components/common/PermissionGate";
import ExportModal from "../../../components/common/ExportModal";
import {
  exportToCSV,
  generateExportFilename,
  downloadFromBackend,
} from "../../../utils/exportUtils";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import DateRangeFilter from "../../../components/common/DateRangeFilter";

const LeadActivities = ({ onViewLead }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isSalesExecutive = user?.role === USER_ROLES.SALES_EXECUTIVE;

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    type: 'all',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  }), []);

  const {
    filters: activityFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateActivityFilter,
    updatePage: updateActivityPage,
    updateLimit: updateActivityLimit,
    updateSort: updateActivitySort,
  } = useUrlTableState(defaultFilters);

  const activeTab = activityFilters.type || 'all';
  const setActiveTab = useCallback((tabVal) => {
    updateActivityFilter({ type: tabVal });
  }, [updateActivityFilter]);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const dateRange = useMemo(() => ({
    startDate: activityFilters.startDate || '',
    endDate: activityFilters.endDate || ''
  }), [activityFilters.startDate, activityFilters.endDate]);

  const setDateRange = useCallback((range) => {
    updateActivityFilter({ startDate: range.startDate, endDate: range.endDate });
  }, [updateActivityFilter]);

  const currentPage = activityFilters.page;
  const setCurrentPage = useCallback((page) => {
    updateActivityPage(page);
  }, [updateActivityPage]);

  const itemsPerPage = activityFilters.limit;
  const setItemsPerPage = useCallback((limit) => {
    updateActivityLimit(limit);
  }, [updateActivityLimit]);

  const sortBy = activityFilters.sortBy || 'createdAt';
  const sortOrder = activityFilters.sortOrder || 'desc';

  const [showLeadSelectionModal, setShowLeadSelectionModal] = useState(false);

  // API state
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalActivities: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10,
  });
  const [deletingActivityId, setDeletingActivityId] = useState(null);

  // New states for export and selection
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);




  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== "" ||
      activeTab !== "all" ||
      dateRange.startDate !== "" ||
      dateRange.endDate !== ""
    );
  }, [searchTerm, activeTab, dateRange]);

  // Sorting handler
  const handleSort = useCallback((column) => {
    if (sortBy === column) {
      updateActivitySort(column, sortOrder === "asc" ? "desc" : "asc");
    } else {
      updateActivitySort(column, "asc");
    }
  }, [sortBy, sortOrder, updateActivitySort]);

  const getSortedActivities = () => {
    if (!sortBy || !activities || activities.length === 0) return activities;

    return [...activities].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === "leadId") {
        aValue = a.lead?.leadId || a.lead?.id || "";
        bValue = b.lead?.leadId || b.lead?.id || "";
      } else if (sortBy === "leadName") {
        aValue = getLeadName(a.lead).toLowerCase();
        bValue = getLeadName(b.lead).toLowerCase();
      } else if (sortBy === "title") {
        aValue = (a.title || "").toLowerCase();
        bValue = (b.title || "").toLowerCase();
      } else if (sortBy === "type") {
        aValue = (a.type || "").toLowerCase();
        bValue = (b.type || "").toLowerCase();
      } else if (sortBy === "performedAt") {
        aValue = new Date(getActivityTime(a)).getTime();
        bValue = new Date(getActivityTime(b)).getTime();
      } else if (sortBy === "createdAt") {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else if (sortBy === "status") {
        aValue = (a.status || a.outcome || "completed").toLowerCase();
        bValue = (b.status || b.outcome || "completed").toLowerCase();
      } else {
        aValue = a[sortBy] || "";
        bValue = b[sortBy] || "";
      }

      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  // Clear all filters and search
  const handleClearFilters = () => {
    updateActivityFilter({
      search: '',
      type: 'all',
      startDate: '',
      endDate: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
  };

  // Fetch activities from API
  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        type: activeTab !== "all" ? activeTab : undefined,
        search: searchTerm.trim() || undefined,
        page: currentPage,
        limit: itemsPerPage,
      };

      if (dateRange.startDate) filters.startDate = dateRange.startDate;
      if (dateRange.endDate) filters.endDate = dateRange.endDate;

      // Remove undefined values
      Object.keys(filters).forEach(
        (key) => filters[key] === undefined && delete filters[key],
      );

      const result = await leadActivityService.getAllActivities(filters);

      if (result.success) {
        setActivities(result.activities || []);
        setPagination(
          result.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalActivities: 0,
            hasNextPage: false,
            hasPrevPage: false,
            limit: itemsPerPage,
          },
        );
      } else {
        toast.error(result.error || "Failed to load activities");
        setActivities([]);
      }
    } catch (error) {

      toast.error("An error occurred while loading activities");
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, dateRange, currentPage, itemsPerPage]);

  // Load activities when filters, page, or items per page changes
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Reset selection when page changes
  useEffect(() => {
    setSelectedRows(new Set());
  }, [currentPage]);



  // Helper functions
  const getLeadName = (lead) => {
    if (!lead) return "Unknown Lead";
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else {
      return "Unknown Lead";
    }
  };

  const getPerformerName = (performedBy) => {
    if (!performedBy) return "Unknown User";
    if (typeof performedBy === "string") return performedBy;
    if (performedBy.firstName && performedBy.lastName) {
      return `${performedBy.firstName} ${performedBy.lastName}`;
    } else if (performedBy.firstName) {
      return performedBy.firstName;
    } else if (performedBy.email) {
      return performedBy.email;
    }
    return "Unknown User";
  };

  // Date formatting helpers using the imported formatDate
  const _formatDateOnly = (dateString) => {
    return formatDate(dateString, { includeTime: false });
  };

  const _formatTimeOnly = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "N/A";
    }
  };

  // Helper function to get the correct activity time
  // Priority: performedAt (when call was initiated) > callMetadata times > createdAt
  // This ensures consistent time display before and after webhook updates
  const getActivityTime = (activity) => {
    // Always prioritize performedAt first - this is when the call was actually initiated
    if (activity.performedAt) {
      return new Date(activity.performedAt).toISOString();
    }

    // For automated calls, fallback to callMetadata times if performedAt is not available
    if (activity.isAutomated && activity.callMetadata) {
      // Priority order: startTime > legBStartTime > legAPickedTime > legBPickedTime
      const callTime =
        activity.callMetadata.startTime ||
        activity.callMetadata.legBStartTime ||
        activity.callMetadata.legAPickedTime ||
        activity.callMetadata.legBPickedTime;

      if (callTime) {
        try {
          const date = new Date(callTime);
          if (!isNaN(date.getTime())) {
            return date.toISOString();
          }
        } catch (error) {

        }
      }
    }

    // Final fallback to createdAt
    return activity.createdAt || new Date().toISOString();
  };

  const formatDuration = (minutes, callDurationSeconds = null) => {
    // If callDurationSeconds is provided (from callMetadata), use exact seconds
    if (callDurationSeconds !== null && callDurationSeconds !== undefined) {
      const totalSeconds = Math.floor(callDurationSeconds);
      const hours = Math.floor(totalSeconds / 3600);
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;

      if (hours > 0) {
        if (mins === 0 && secs === 0) {
          return `${hours} hour${hours !== 1 ? "s" : ""}`;
        } else if (secs === 0) {
          return `${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${mins !== 1 ? "s" : ""}`;
        } else {
          return `${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
        }
      } else if (mins > 0) {
        if (secs === 0) {
          return `${mins} minute${mins !== 1 ? "s" : ""}`;
        } else {
          return `${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
        }
      } else {
        return `${secs} second${secs !== 1 ? "s" : ""}`;
      }
    }

    // Fallback to minutes (for non-automated calls or if callDuration not available)
    if (!minutes) return "";

    // Convert minutes to exact seconds for precise display
    const totalSeconds = Math.floor(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) {
        return `${hours} hour${hours !== 1 ? "s" : ""}`;
      } else if (secs === 0) {
        return `${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${mins !== 1 ? "s" : ""}`;
      } else {
        return `${hours} hour${hours !== 1 ? "s" : ""} ${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
      }
    } else if (mins > 0) {
      if (secs === 0) {
        return `${mins} minute${mins !== 1 ? "s" : ""}`;
      } else {
        return `${mins} minute${mins !== 1 ? "s" : ""} ${secs} second${secs !== 1 ? "s" : ""}`;
      }
    } else {
      return `${secs} second${secs !== 1 ? "s" : ""}`;
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case "call":
        return Phone;
      case "email":
        return Mail;
      case "whatsapp":
        return MessageSquare;
      case "meeting":
        return Calendar;
      case "linkedin":
        return Linkedin;
      default:
        return Clock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case "call":
        return "text-[#0F2942]";
      case "email":
        return "text-green-600";
      case "whatsapp":
        return "text-green-500";
      case "meeting":
        return "text-purple-600";
      case "linkedin":
        return "text-sky-700";
      default:
        return "text-gray-600";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
      case "sent":
        return "bg-green-100 text-green-800";
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "rescheduled":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Helper function to map RP Digital call status to UI display text
  const getCallStatusDisplay = (status) => {
    if (!status) return null;
    const statusUpper = status.toUpperCase();
    const statusMap = {
      ANSWER: "Answered",
      PICKED: "Answered", // Picked means call was answered
      NOANSWER: "Missed / No Answer",
      BUSY: "Busy",
      "CANCEL-AGENT": "Cancelled by Agent",
      "CANCEL-CUSTOMER": "Cancelled by Customer",
      FAILED: "Failed",
      PENDING: "Pending",
    };
    return statusMap[statusUpper] || status;
  };

  // Helper function to get call status badge color
  const getCallStatusColor = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case "ANSWER":
        return "bg-green-100 text-green-800";
      case "PICKED":
        return "bg-green-100 text-green-800"; // Picked means answered
      case "NOANSWER":
        return "bg-yellow-100 text-yellow-800";
      case "BUSY":
        return "bg-orange-100 text-orange-800";
      case "CANCEL-AGENT":
        return "bg-red-100 text-red-800";
      case "CANCEL-CUSTOMER":
        return "bg-red-100 text-red-800";
      case "FAILED":
        return "bg-red-100 text-red-800";
      case "PENDING":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleLogActivity = () => {
    // Show lead selection modal
    setShowLeadSelectionModal(true);
  };



  // View Activity functionality - navigate to detail page
  const handleViewActivity = (activityId) => {
    navigate(`/activity_detail/${activityId}`, { state: { from: location.pathname + location.search } });
  };

  // Edit Activity functionality - navigate to LogActivity in edit mode
  const handleEditActivity = (activityId) => {
    // Find the activity to get the lead ID
    const activity = activities.find(
      (a) => a._id === activityId || a.activityId === activityId,
    );

    if (activity && activity.lead) {
      const leadId = activity.lead._id || activity.lead.id;
      if (leadId) {
        navigate(`/log_activity/${leadId}?edit=true&activityId=${activityId}`, { state: { from: location.pathname + location.search } });
      } else {
        toast.error("Cannot edit: Lead information not available");
      }
    } else {
      toast.error("Cannot edit: Activity not found");
    }
  };

  const mapActivitiesForExport = (activitiesToExport) => {
    return activitiesToExport.map((activity) => ({
      "Activity Date": formatDate(getActivityTime(activity)),
      "Lead ID": activity.lead?.leadId || "N/A",
      "Lead Name": getLeadName(activity.lead),
      Company: activity.lead?.company || "N/A",
      Email: activity.lead?.email || "N/A",
      "Activity Type":
        (activity.type || "").charAt(0).toUpperCase() +
        (activity.type || "").slice(1),
      "Performed By": activity.performedBy
        ? `${activity.performedBy.firstName || ""} ${activity.performedBy.lastName || ""}`.trim() +
          (activity.performedBy.email ? ` (${activity.performedBy.email})` : "")
        : "N/A",
      "Updated At": formatDate(activity.updatedAt || activity.createdAt),
      "Updated By": activity.updatedBy
        ? `${activity.updatedBy.firstName || ""} ${activity.updatedBy.lastName || ""}`.trim()
        : "N/A",
    }));
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const activitiesToExport = activities.filter((activity) => {
        const id = activity._id || activity.activityId;
        return selectedRows.has(id);
      });
      const exportData = mapActivitiesForExport(activitiesToExport);
      exportToCSV(exportData, "activities", {
        title: "Lead Activity List",
        type: "Selected Activities",
      });
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const exportData = mapActivitiesForExport(activities);
    exportToCSV(exportData, "activities_page", {
      title: "Lead Activity List",
      type: "Current Page",
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append("startDate", startDate);
      if (endDate) queryParams.append("endDate", endDate);
      if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());
      if (activeTab !== "all") queryParams.append("type", activeTab);

      const url = `${process.env.REACT_APP_API_BASE_URL}/crm/activities/export?${queryParams.toString()}`;
      const filename = generateExportFilename("activities_range");
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success("Export started successfully");
    } catch (error) {

      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("type", "All Data");
      if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());
      if (activeTab !== "all") queryParams.append("type", activeTab);

      const url = `${process.env.REACT_APP_API_BASE_URL}/crm/activities/export?${queryParams.toString()}`;
      const filename = generateExportFilename("all_activities");
      await downloadFromBackend(url, filename);

      setShowExportModal(false);
      toast.success("All activities export started");
    } catch (error) {

      toast.error("An error occurred during export");
    } finally {
      setExportLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = activities.map(
        (activity) => activity._id || activity.activityId,
      );
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Handle delete activity - EXACTLY like detail view
  const handleDeleteActivity = async (activityId) => {


    // Validate activityId first
    if (!activityId) {

      toast.error("Activity ID is missing");
      return;
    }

    // Find the activity to show details in confirmation (same as detail view)
    const activity = activities.find(
      (a) => a._id === activityId || a.activityId === activityId,
    );


    if (!activity) {

      toast.error("Activity not found");
      return;
    }

    // Use same confirmation message as detail view
    const confirmMessage = `Are you sure you want to delete this activity? This action cannot be undone.`;


    // CRITICAL: Show confirmation dialog FIRST - handle both sync and async cases
    // Some environments may return a Promise, so we handle both
    let confirmed;
    const confirmResult = window.confirm(confirmMessage);

    // Check if it's a Promise (async) or boolean (sync)
    if (confirmResult instanceof Promise) {

      confirmed = await confirmResult;
    } else {
      confirmed = confirmResult;
    }



    // If user clicked Cancel, stop here - do NOT proceed
    if (confirmed !== true && confirmed !== "true") {

      return; // User cancelled, exit immediately - NO API CALL
    }

    // ONLY if user clicked OK (confirmed === true), proceed with delete
    // This code only runs AFTER user confirms


    // Set deleting state to prevent multiple calls
    setDeletingActivityId(activityId);


    try {

      const result = await leadActivityService.deleteActivity(activityId);


      if (result.success) {

        toast.success("Activity deleted successfully");

        // Refresh the list
        fetchActivities();
      } else {

        toast.error(result.error || "Failed to delete activity");
      }
    } catch (error) {

      toast.error("An error occurred while deleting the activity");
    } finally {

      // Clear deleting state
      setDeletingActivityId(null);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const selectedIds = Array.from(selectedRows);
      let successCount = 0;
      let lastError = null;

      // Activities are deleted one by one as there's no bulk delete endpoint in leadActivityService
      for (const activityId of selectedIds) {
        const result = await leadActivityService.deleteActivity(activityId);
        if (result.success) {
          successCount++;
        } else {
          lastError = result.error;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully deleted ${successCount} activities`);
        setSelectedRows(new Set());
        fetchActivities();
      }

      if (successCount < selectedIds.length) {
        toast.error(lastError || "Some activities could not be deleted");
      }
      setShowBulkDeleteModal(false);
    } catch (error) {

      toast.error("An error occurred during bulk deletion");
    } finally {
      setIsBulkDeleting(false);
    }
  };



  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewLead = (leadId) => {
    if (onViewLead) {
      onViewLead(leadId);
    } else {
      navigate(`/lead_detail_360/${leadId}`, { state: { from: location.pathname + location.search } });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <Activity className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Lead Activities</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Track and manage all lead interactions and activities</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <PermissionGate permission="lead_management" action="read">
            <button
              className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleExportClick}
            >
              <Download className="h-4 w-4" />
              <span>{selectedRows.size > 0 ? `Export (${selectedRows.size})` : "Export"}</span>
            </button>
          </PermissionGate>
          {selectedRows.size > 0 && (
            <PermissionGate permission="lead_management" action="delete">
              <button
                className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
                onClick={handleBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? <Clock className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span>{isBulkDeleting ? "Deleting..." : `Delete (${selectedRows.size})`}</span>
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="lead_management" action="create">
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleLogActivity}
            >
              <Plus className="h-4 w-4" />
              <span>Log Activity</span>
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Lead ID, Lead Name, or Company Name..."
              value={searchTerm}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(
                  e,
                  setSearchTerm,
                  FIELD_LIMITS.search,
                );
                setSearchTerm(sanitized);
              }}
              onKeyDown={(e) => {
                // Prevent form submission on Enter key
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              maxLength={FIELD_LIMITS.search}
              autoFocus
              className="input-field pl-10"
              autoComplete="off"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="input-field w-full sm:w-auto"
            >
              <option value="all">All Activities</option>
              <option value="call">Calls</option>
              <option value="email">Emails</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="meeting">Meetings</option>
              <option value="linkedin">LinkedIn</option>
            </select>

            {/* Removed Newest First dropdown */}
            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={setDateRange}
              />
            </div>
            {hasActiveFilters && (
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
                className="flex-shrink-0 whitespace-nowrap"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Activities List - Table View */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
            <p className="text-gray-600">Loading activities...</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table
              className="w-full divide-y divide-gray-200"
              style={{ tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "48px" }} />
                <col style={{ width: "110px" }} />
                <col style={{ width: "180px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "180px" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "150px" }} />
                <col style={{ width: "120px" }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left w-12">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                      checked={
                        activities.length > 0 &&
                        selectedRows.size === activities.length
                      }
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("leadId")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead ID</span>
                      {sortBy === "leadId" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("leadName")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead</span>
                      {sortBy === "leadName" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("type")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Type</span>
                      {sortBy === "type" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    Performed By
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("createdAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortBy === "createdAt" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort("updatedAt")}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Updated At</span>
                      {sortBy === "updatedAt" ? (
                        sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>

                  <th
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activities.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="p-3 bg-gray-100 rounded-full mb-4">
                          <Clock className="h-6 w-6 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No activities found
                        </h3>
                        <p className="text-gray-500 mb-4">
                          {searchTerm || activeTab !== "all"
                            ? "Try adjusting your filters or search terms."
                            : "No activities have been logged yet."}
                        </p>
                        <Button
                          variant="primary"
                          icon={<Plus className="h-4 w-4" />}
                          onClick={handleLogActivity}
                          className="bg-agrosaf-green hover:bg-agrosaf-darkGreen"
                        >
                          Log Activity
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  getSortedActivities().map((activity) => {
                    const activityId = activity._id || activity.activityId;
                    const isSelected = selectedRows.has(activityId);
                    return (
                      <tr
                        key={activityId}
                        className={`hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                      >
                        <td className="px-4 py-4 whitespace-nowrap align-top">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-agrosaf-green border-gray-300 rounded focus:ring-agrosaf-green"
                            checked={isSelected}
                            onChange={() => handleSelectRow(activityId)}
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap align-top">
                          {activity.lead ? (
                            <button
                              type="button"
                              className="text-xs text-left text-[#0F2942] font-semibold font-mono hover:text-blue-800 hover:underline transition-colors focus:outline-none"
                              title={`View details for ${activity.lead.leadId || 'No ID'}`}
                              onClick={() =>
                                handleViewLead(
                                  activity.lead._id || activity.lead.id
                                )
                              }
                            >
                              {activity.lead.leadId ||
                                activity.lead.id ||
                                "No ID"}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 font-medium font-mono">
                              No ID
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-4 max-w-0 align-top">
                          {activity.lead ? (
                            <div className="flex items-center space-x-3 min-w-0">
                              <div className="min-w-0 flex-1 overflow-hidden">
                                <div
                                  className="text-sm font-medium text-gray-900 truncate capitalize"
                                  title={getLeadName(activity.lead)}
                                >
                                  {getLeadName(activity.lead)}
                                </div>
                                <div
                                  className="text-sm text-gray-500 truncate first-letter:capitalize"
                                  title={activity.lead.company || "No company"}
                                >
                                  {activity.lead.company || "No company"}
                                </div>
                                {activity.lead.email && (
                                  <div
                                    className="text-xs text-gray-400 truncate lowercase"
                                    title={activity.lead.email}
                                  >
                                    {activity.lead.email || "No email"}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">
                              No lead
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-4 align-top">
                          <div className="flex flex-col gap-1.5 min-w-0">
                            <span className="text-sm text-gray-900 capitalize">
                              {activity.type}
                            </span>
                          </div>
                        </td>

                        <td className="px-4 py-4 align-top">
                          {activity.performedBy ? (
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900 capitalize">
                                {`${activity.performedBy.firstName || ""} ${activity.performedBy.lastName || ""}`.trim()}
                              </span>
                              {activity.performedBy.email && (
                                <span className="text-xs text-gray-400 lowercase">
                                  {activity.performedBy.email}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">N/A</span>
                          )}
                        </td>

                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 align-top">
                          {formatDate(activity.createdAt)}
                        </td>

                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 align-top">
                          <div className="flex flex-col">
                            <span>{formatDate(activity.updatedAt || activity.createdAt)}</span>
                            {activity.updatedBy && (
                              <span className="text-xs text-gray-400" title={activity.updatedBy.email}>
                                By: {`${activity.updatedBy.firstName || ""} ${activity.updatedBy.lastName || ""}`.trim()}
                              </span>
                            )}
                          </div>
                        </td>
                        {/* <td className="px-4 py-4 whitespace-nowrap">
                          {(() => {
                            // For meetings, show duration if available
                            if (
                              activity.type === "meeting" &&
                              activity.duration
                            ) {
                              return (
                                <span className="text-sm text-gray-900">
                                  {formatDuration(activity.duration)}
                                </span>
                              );
                            }

                            // For calls, only show talkDuration from callMetadata
                            if (activity.type === "call") {
                              const talkDuration =
                                activity.callMetadata?.talkDuration;

                              if (
                                talkDuration !== null &&
                                talkDuration !== undefined &&
                                talkDuration > 0
                              ) {
                                return (
                                  <span className="text-sm text-gray-900">
                                    {formatDuration(null, talkDuration)}
                                  </span>
                                );
                              }

                              return (
                                <span className="text-sm text-gray-400">-</span>
                              );
                            }

                            // For other activity types, show duration if available
                            if (activity.duration) {
                              return (
                                <span className="text-sm text-gray-900">
                                  {formatDuration(activity.duration)}
                                </span>
                              );
                            }

                            return (
                              <span className="text-sm text-gray-400">-</span>
                            );
                          })()}
                        </td> */}

                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium align-top">
                          <div className="flex items-center space-x-2">
                            <PermissionGate
                              permission="lead_management"
                              action="read"
                            >
                              <button
                                onClick={() => handleViewActivity(activity._id)}
                                className="text-[#0F2942] hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </PermissionGate>

                            {!isSalesExecutive && (
                              <PermissionGate
                                permission="lead_management"
                                action="update"
                              >
                                <button
                                  onClick={() => handleEditActivity(activity._id)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Activity"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}



                            {!isSalesExecutive && (
                              <PermissionGate
                                permission="lead_management"
                                action="delete"
                              >
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // IMPORTANT: handleDeleteActivity will show confirmation FIRST
                                    // The API will ONLY be called AFTER user clicks OK in confirmation dialog
                                    // This is a blocking call - execution stops until user responds
                                    handleDeleteActivity(activity._id);
                                  }}
                                  disabled={deletingActivityId === activity._id}
                                  className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete Activity"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </PermissionGate>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination - Same as Lead List & Filters */}
      {(() => {
        const activitiesTotalItems = Number(
          pagination?.totalActivities || activities.length || 0,
        );
        const activitiesTotalPages = Number(pagination?.totalPages || 1);
        const effectivePaginationLimit = pagination?.limit || itemsPerPage || 1;
        // Show pagination if there's more than one page
        const shouldShowPagination = !loading && activitiesTotalPages > 1;
        const handleActivitiesPageChange = (page) => {
          setCurrentPage(page);
          window.scrollTo({ top: 0, behavior: "smooth" });
        };

        return shouldShowPagination ? (
          <PaginationControls
            currentPage={pagination.currentPage || currentPage || 1}
            totalPages={activitiesTotalPages}
            totalItems={activitiesTotalItems}
            limit={effectivePaginationLimit}
            hasNextPage={pagination.hasNextPage !== false && (pagination.currentPage || 1) < activitiesTotalPages}
            hasPrevPage={pagination.hasPrevPage !== false && (pagination.currentPage || 1) > 1}
            onPageChange={handleActivitiesPageChange}
            onLimitChange={(limit) => handleItemsPerPageChange(limit)}
            limitOptions={[1, 3, 10, 25, 50, 100]}
          />
        ) : null;
      })()}

      {/* Activity Detail Modal */}

      {/* Lead Selection Modal */}
      <LeadSelectionModal
        isOpen={showLeadSelectionModal}
        onClose={() => setShowLeadSelectionModal(false)}
        onSelect={(leadId) => navigate(`/log_activity/${leadId}`, { state: { from: location.pathname + location.search } })}
      />
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title="Export Activities"
      />

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[110] !mt-0 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-auto shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                Bulk Delete Activities
              </h3>
              <button
                onClick={() => !isBulkDeleting && setShowBulkDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
                disabled={isBulkDeleting}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <span className="font-bold text-gray-900">{selectedRows.size}</span> selected activities? This action cannot be undone.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                className="btn-cancel"
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
              >
                Cancel
              </button>
              <button
                className="btn-create flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed !bg-red-600 hover:!bg-red-700"
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? <Clock className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span>{isBulkDeleting ? "Deleting..." : `Delete All Selected (${selectedRows.size})`}</span>
              </button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default LeadActivities;
